import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

const documentGroups = [
  {
    title: "Simple Step Counter for Android",
    description: "Legal documents for the Android app.",
    documents: [
      {
        slug: "privacy-policy",
        title: "Privacy Policy",
        summary: "How the Android app handles data and privacy.",
        file: "/legal-documents/android/privacy-policy.txt",
      },
      {
        slug: "terms-conditions",
        title: "Terms & Conditions",
        summary: "Rules and terms for using the Android app.",
        file: "/legal-documents/android/terms-conditions.txt",
      },
      {
        slug: "disclaimer",
        title: "Disclaimer",
        summary: "Accuracy, health-related limitations, and liability.",
        file: "/legal-documents/android/disclaimer.txt",
      },
      {
        slug: "imprint",
        title: "Imprint / Legal Notice",
        summary: "Provider and contact information.",
        file: "/legal-documents/android/imprint.txt",
      },
      {
        slug: "open-source-licenses",
        title: "Open-Source Licenses",
        summary: "Notices for third-party software used by the Android app.",
        file: "/legal-documents/android/open-source-licenses.txt",
      },
    ],
  },
  {
    title: "Simple Step Counter for iOS",
    description: "Legal documents for the iOS app.",
    documents: [
      {
        slug: "privacy-policy",
        title: "Privacy Policy",
        summary: "How the iOS app handles data and privacy.",
        file: "/legal-documents/ios/privacy-policy.txt",
      },
      {
        slug: "disclaimer",
        title: "Disclaimer",
        summary: "Accuracy, health-related limitations, and liability.",
        file: "/legal-documents/ios/disclaimer.txt",
      },
      {
        slug: "imprint",
        title: "Imprint / Legal Notice",
        summary: "Provider and contact information.",
        file: "/legal-documents/ios/imprint.txt",
      },
      {
        slug: "open-source-licenses",
        title: "Open-Source Licenses",
        summary: "Notices for third-party software used by the iOS app.",
        file: "/legal-documents/ios/open-source-licenses.txt",
      },
    ],
  },
  {
    title: "Website",
    description: "Legal documents for this website.",
    documents: [
      {
        slug: "privacy-policy",
        title: "Website Privacy Policy",
        summary: "How this website handles server logs, analytics, and cookies.",
        file: "/legal-documents/website/privacy-policy.txt",
      },
      {
        slug: "imprint",
        title: "Imprint / Legal Notice",
        summary: "Provider and contact information for this website.",
        file: "/legal-documents/website/imprint.txt",
      },
    ],
  },
];

const groupSlugs = {
  android: documentGroups[0],
  ios: documentGroups[1],
  website: documentGroups[2],
};

function legalDocumentPath(groupSlug, documentSlug) {
  return `/legal/${groupSlug}/${documentSlug}`;
}

function isNumberedHeading(line) {
  return /^\d+\.\s+\S/.test(line);
}

function isSectionLabel(line) {
  return line.length <= 40 && !line.endsWith(".") && !line.includes(":");
}

function linkify(text) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, index) => {
    if (!/^https?:\/\//.test(part)) return part;

    const trailingPunctuation = part.match(/[.,;:!?)]*$/)?.[0] || "";
    const href = trailingPunctuation ? part.slice(0, -trailingPunctuation.length) : part;

    return (
      <React.Fragment key={`${part}-${index}`}>
        <a href={href} target="_blank" rel="noopener noreferrer" className="inline-link">
          {href}
        </a>
        {trailingPunctuation}
      </React.Fragment>
    );
  });
}

function LegalText({ text, contextLabel }) {
  const lines = text.split("\n");

  return (
    <div className="legal-text">
      {contextLabel && <p className="legal-context-label">{contextLabel}</p>}
      {lines.map((rawLine, index) => {
        const line = rawLine.trim();
        const isFirst = index === 0;
        const afterBlank = index === 0 || lines[index - 1].trim() === "";

        if (!line) {
          return <div key={index} className="legal-text-spacer" aria-hidden="true" />;
        }

        if (isFirst) {
          return <h1 key={index} className="legal-document-title">{line}</h1>;
        }

        if (line.toLowerCase().startsWith("last updated:")) {
          return <p key={index} className="legal-updated">{line}</p>;
        }

        if (isNumberedHeading(line) || (afterBlank && isSectionLabel(line))) {
          return <h2 key={index} className="legal-document-heading">{line}</h2>;
        }

        if (line.startsWith("- ")) {
          return (
            <p key={index} className="legal-bullet">
              <span aria-hidden="true">•</span>
              <span>{linkify(line.slice(2))}</span>
            </p>
          );
        }

        return <p key={index}>{linkify(line)}</p>;
      })}
    </div>
  );
}

export function LegalCenter() {
  return (
    <div className="page-container">
      <div className="page-content">
        <h1 className="page-title">Legal & Privacy</h1>
        <div className="legal-center">
          {Object.entries(groupSlugs).map(([groupSlug, group]) => (
            <section className="legal-group" key={group.title}>
              <div className="legal-group-header">
                <h2>{group.title}</h2>
                <p>{group.description}</p>
              </div>
              <div className="legal-document-grid">
                {group.documents.map((document) => (
                  <Link
                    key={document.slug}
                    to={legalDocumentPath(groupSlug, document.slug)}
                    className="legal-document-card"
                  >
                    <span>{document.title}</span>
                    <p>{document.summary}</p>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export function LegalDocumentPage() {
  const { platform, documentSlug } = useParams();
  const [text, setText] = useState("");
  const [status, setStatus] = useState("loading");

  const legalPage = useMemo(() => {
    const group = groupSlugs[platform];
    const document = group?.documents.find((item) => item.slug === documentSlug);

    if (!group || !document) return null;

    return { group, document };
  }, [platform, documentSlug]);

  const document = legalPage?.document;

  useEffect(() => {
    let cancelled = false;

    async function loadDocument() {
      if (!document) {
        setStatus("missing");
        return;
      }

      setStatus("loading");

      try {
        const response = await fetch(`${process.env.PUBLIC_URL}${document.file}`);

        if (!response.ok) {
          throw new Error(`Could not load ${document.file}`);
        }

        const body = await response.text();

        if (!cancelled) {
          setText(body);
          setStatus("ready");
        }
      } catch (error) {
        if (!cancelled) {
          setStatus("error");
        }
      }
    }

    loadDocument();

    return () => {
      cancelled = true;
    };
  }, [document]);

  if (!document || status === "missing") {
    return (
      <div className="page-container">
        <div className="page-content">
          <h1 className="page-title">Document Not Found</h1>
          <p className="legal-status">The requested legal document does not exist.</p>
          <Link to="/legal" className="inline-link">Back to Legal & Privacy</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content">
        <Link to="/legal" className="legal-back-link">Back to Legal & Privacy</Link>
        {status === "loading" && <p className="legal-status">Loading legal document...</p>}
        {status === "error" && <p className="legal-status">This legal document could not be loaded.</p>}
        {status === "ready" && <LegalText text={text} contextLabel={legalPage.group.title} />}
      </div>
    </div>
  );
}
